import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {supabase} from '../lib/supabase/client';
import {usePwaInstall} from '../contexts/PwaInstallContext';
import Button from './ui/Button';
import {useLanguage} from '../contexts/LanguageContext';

const {
  FiChevronDown,
  FiDownload,
  FiLoader,
  FiLogOut,
  FiPlusSquare,
  FiShare,
  FiSettings,
  FiTrash2,
  FiUser,
  FiX
} = FiIcons;

function getInitials(session) {
  const name =
    session?.user?.user_metadata?.full_name ||
    session?.user?.email ||
    'User';

  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function UserMenu({session}) {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const helpTitleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const {experience, requestInstall} = usePwaInstall();
  const {t} = useLanguage();

  const email = session?.user?.email || '';
  const displayName =
    session?.user?.user_metadata?.full_name || email || 'Account';
  const initials = getInitials(session);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setShowInstallHelp(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSignOut = async () => {
    if (!supabase || isSigningOut) {
      return;
    }

    setIsSigningOut(true);

    try {
      const {error} = await supabase.auth.signOut();

      if (error) {
        console.error('SilaTrail sign-out failed:', error);
        setIsSigningOut(false);
        return;
      }

      setOpen(false);
      navigate('/login', {replace: true});
    } catch (error) {
      console.error('SilaTrail sign-out failed:', error);
      setIsSigningOut(false);
    }
  };

  const handleInstall = async () => {
    setOpen(false);
    if (experience === 'ios-help') {
      setShowInstallHelp(true);
      window.setTimeout(() => helpTitleRef.current?.focus(), 0);
      return;
    }
    await requestInstall();
  };

  const menuItemClasses =
    'flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 hover:text-teal-700';

  return (
    <div ref={menuRef} className="relative">
      {open && (
        <div
          role="menu"
          aria-label={t('Account')}
          className="absolute bottom-full right-0 z-50 mb-3 min-w-[220px] origin-bottom-right animate-fade-in-up overflow-hidden rounded-xl border border-gray-100 bg-white opacity-100 shadow-xl transition-all duration-200 ease-out"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="truncate text-sm font-semibold text-gray-900">
              {displayName}
            </p>

            {email && (
              <p className="mt-1 truncate text-xs text-gray-500">
                {email}
              </p>
            )}
          </div>

          <div className="space-y-1 p-2">
            <Link
              to="/settings/profile"
              role="menuitem"
              onClick={() => setOpen(false)}
              className={menuItemClasses}
            >
              <SafeIcon
                icon={FiUser}
                className="h-4 w-4 shrink-0 text-gray-500"
              />
              <span className="leading-5">{t('Profile')}</span>
            </Link>

            <Link
              to="/settings/business"
              role="menuitem"
              onClick={() => setOpen(false)}
              className={menuItemClasses}
            >
              <SafeIcon
                icon={FiSettings}
                className="h-4 w-4 shrink-0 text-gray-500"
              />
              <span className="leading-5">{t('Business')}</span>
            </Link>

            <Link
              to="/settings/billing"
              role="menuitem"
              onClick={() => setOpen(false)}
              className={menuItemClasses}
            >
              <SafeIcon
                icon={FiSettings}
                className="h-4 w-4 shrink-0 text-gray-500"
              />
              <span className="leading-5">{t('Billing')}</span>
            </Link>

            {(experience === 'native' || experience === 'ios-help') && (
              <button
                type="button"
                role="menuitem"
                onClick={handleInstall}
                className={menuItemClasses}
              >
                <SafeIcon
                  icon={FiDownload}
                  className="h-4 w-4 shrink-0 text-gray-500"
                />
                <span className="leading-5">{t('Install SilaTrail')}</span>
              </button>
            )}
          </div>

          <div className="border-t border-gray-100 p-2">
            <Link
              to="/settings/delete-account"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm font-semibold text-red-600 transition-colors duration-200 hover:bg-red-50"
            >
              <SafeIcon
                icon={FiTrash2}
                className="h-4 w-4 shrink-0"
              />
              <span className="leading-5">{t('Delete account')}</span>
            </Link>

            <button
              type="button"
              role="menuitem"
              disabled={isSigningOut}
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm font-semibold text-red-600 transition-colors duration-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SafeIcon
                icon={isSigningOut ? FiLoader : FiLogOut}
                className={`h-4 w-4 shrink-0 ${
                  isSigningOut ? 'animate-spin' : ''
                }`}
              />
              <span className="leading-5">
                {t(isSigningOut ? 'Signing out…' : 'Sign out')}
              </span>
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('Account')}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white p-1.5 pr-2.5 text-left transition-all duration-200 hover:border-teal-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 active:scale-[0.98]"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-teal-600 text-xs font-bold text-white">
          {initials}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-gray-900">
            {displayName}
          </span>
          <span className="block text-xs text-gray-500">{t('Account')}</span>
        </span>

        <SafeIcon
          icon={FiChevronDown}
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {showInstallHelp && (
        <div
          className="fixed inset-0 z-[90] overflow-y-auto bg-ink/45 px-4 py-8 backdrop-blur-sm"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowInstallHelp(false);
          }}
        >
          <div className="mx-auto flex min-h-full max-w-md items-center">
            <section
              className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="install-silatrail-title"
            >
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
                <div>
                  <p className="text-sm font-semibold text-primary">SilaTrail on iPhone</p>
                  <h2
                    id="install-silatrail-title"
                    ref={helpTitleRef}
                    tabIndex="-1"
                    className="mt-1 text-xl font-bold tracking-tight text-ink outline-none"
                  >
                    Add the app to your Home Screen
                  </h2>
                </div>
                <button type="button" aria-label="Close install instructions" onClick={() => setShowInstallHelp(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-ink">
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
              </div>

              <ol className="space-y-4 px-6 py-6 text-sm text-slate-600">
                <li className="flex gap-3"><span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-mint font-bold text-primary">1</span><span className="pt-1.5">Open SilaTrail in <strong className="text-ink">Safari</strong>.</span></li>
                <li className="flex gap-3"><span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-mint font-bold text-primary">2</span><span className="flex items-center gap-2 pt-1.5">Tap <SafeIcon icon={FiShare} className="h-4 w-4 text-primary" /> <strong className="text-ink">Share</strong>.</span></li>
                <li className="flex gap-3"><span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-mint font-bold text-primary">3</span><span className="flex items-center gap-2 pt-1.5">Choose <SafeIcon icon={FiPlusSquare} className="h-4 w-4 text-primary" /> <strong className="text-ink">Add to Home Screen</strong>, then tap Add.</span></li>
              </ol>

              <div className="border-t border-slate-100 px-6 py-4">
                <Button type="button" className="w-full" onClick={() => setShowInstallHelp(false)}>Got it</Button>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
